import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronDown, ChevronsUpDown, ChevronUp, Search } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import { Checkbox } from '@/components/ui/catalyst/checkbox';
import { Input } from '@/components/ui/catalyst/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { enhancedApi } from '@/services/api/generated/firmsService';

interface FirmBasic {
  id: string;
  name: string;
  status: string;
}

interface FirmSelectionTableProps {
  selectedFirms: Set<string>;
  onSelectionChange: (firms: Set<string>) => void;
  onFirmsLoaded?: (firms: FirmBasic[]) => void;
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'Active', label: 'Active' },
  { value: 'Testing', label: 'Testing' },
  { value: 'Software Only', label: 'Software Only' },
];

const getStatusColor = (
  status: string,
): 'green' | 'yellow' | 'zinc' | 'blue' => {
  switch (status) {
    case 'Active':
      return 'green';
    case 'Testing':
      return 'yellow';
    case 'Software Only':
      return 'blue';
    default:
      return 'zinc';
  }
};

export const FirmSelectionTable: React.FC<FirmSelectionTableProps> = ({
  selectedFirms,
  onSelectionChange,
  onFirmsLoaded,
}) => {
  const { data: firms = [], isLoading } = enhancedApi.useFirmsListQuery();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'name', desc: false },
  ]);
  const [lastClickedIndex, setLastClickedIndex] = React.useState<number | null>(
    null,
  );

  // Notify parent when firms are loaded
  React.useEffect(() => {
    if (firms.length > 0 && onFirmsLoaded) {
      onFirmsLoaded(
        firms.map((f) => ({
          id: f.id,
          name: f.name,
          status: f.status ?? 'Unknown',
        })),
      );
    }
  }, [firms, onFirmsLoaded]);

  // Filter firms by status
  const filteredFirms = React.useMemo(() => {
    return firms.filter((firm) => {
      if (statusFilter !== 'all' && firm.status !== statusFilter) {
        return false;
      }
      return true;
    });
  }, [firms, statusFilter]);

  // Memoize the displayed data to use in callbacks without circular deps
  const displayedData = React.useMemo(
    () =>
      filteredFirms.map((f) => ({
        id: f.id,
        name: f.name,
        status: f.status ?? 'Unknown',
      })),
    [filteredFirms],
  );

  const handleToggleRow = React.useCallback(
    (firmId: string, index: number, shiftKey: boolean) => {
      const newSet = new Set(selectedFirms);

      if (shiftKey && lastClickedIndex !== null) {
        // Shift-click: select range using displayedData instead of table
        const start = Math.min(lastClickedIndex, index);
        const end = Math.max(lastClickedIndex, index);
        const rangeIds = displayedData.slice(start, end + 1).map((f) => f.id);
        rangeIds.forEach((id) => newSet.add(id));
      } else if (newSet.has(firmId)) {
        // Normal click: toggle single item
        newSet.delete(firmId);
      } else {
        newSet.add(firmId);
      }

      setLastClickedIndex(index);
      onSelectionChange(newSet);
    },
    [selectedFirms, lastClickedIndex, onSelectionChange, displayedData],
  );

  const handleToggleAll = React.useCallback(() => {
    // Use displayedData instead of table to avoid circular dependency
    if (
      selectedFirms.size === displayedData.length &&
      displayedData.length > 0
    ) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(displayedData.map((f) => f.id)));
    }
  }, [selectedFirms.size, onSelectionChange, displayedData]);

  /* eslint-disable react/no-unstable-nested-components -- TanStack Table pattern uses render functions for header/cell */
  const columns = React.useMemo<ColumnDef<FirmBasic>[]>(
    () => [
      {
        id: 'select',
        header: () => {
          // Use displayedData instead of table to avoid circular dependency
          const allSelected =
            displayedData.length > 0 &&
            selectedFirms.size === displayedData.length;
          const someSelected =
            selectedFirms.size > 0 && selectedFirms.size < displayedData.length;

          return (
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={handleToggleAll}
              color="blue"
            />
          );
        },
        cell: ({ row }) => (
          <div
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleToggleRow(row.original.id, row.index, e.shiftKey);
            }}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggleRow(row.original.id, row.index, e.shiftKey);
              }
            }}
            role="button"
            tabIndex={0}
            className="cursor-pointer"
          >
            <Checkbox
              checked={selectedFirms.has(row.original.id)}
              onChange={() => {}}
              color="blue"
            />
          </div>
        ),
        size: 40,
        enableSorting: false,
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column }) => {
          const sorted = column.getIsSorted();
          return (
            <button
              type="button"
              onClick={() => column.toggleSorting()}
              className="flex items-center gap-1 font-medium text-left"
            >
              Firm Name
              {sorted === 'asc' && <ChevronUp className="h-4 w-4" />}
              {sorted === 'desc' && <ChevronDown className="h-4 w-4" />}
              {!sorted && <ChevronsUpDown className="h-4 w-4 opacity-50" />}
            </button>
          );
        },
        cell: ({ row }) => (
          <span className="font-medium">{row.original.name}</span>
        ),
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge color={getStatusColor(row.original.status)}>
            {row.original.status}
          </Badge>
        ),
        size: 100,
      },
    ],
    [selectedFirms, handleToggleAll, handleToggleRow, displayedData],
  );
  /* eslint-enable react/no-unstable-nested-components */

  const table = useReactTable({
    data: displayedData,
    columns,
    state: {
      sorting,
      globalFilter: searchQuery,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearchQuery,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: 'includesString',
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm text-zinc-500">Loading firms...</div>
      </div>
    );
  }

  const tableRows = table.getRowModel().rows;

  return (
    <div className="flex flex-col h-full">
      {/* Header with filters */}
      <div className="flex items-center gap-3 p-3 border-b border-zinc-200 dark:border-zinc-800">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            placeholder="Search firms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Selection summary */}
      <div className="px-3 py-2 text-xs text-zinc-600 dark:text-zinc-400 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
        {selectedFirms.size > 0 ? (
          <span className="font-medium text-blue-600 dark:text-blue-400">
            {selectedFirms.size} firm{selectedFirms.size !== 1 ? 's' : ''}{' '}
            selected
          </span>
        ) : (
          <span>
            {tableRows.length} firm{tableRows.length !== 1 ? 's' : ''} shown
          </span>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-white dark:bg-zinc-950 border-b border-zinc-200 dark:border-zinc-800">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={cn(
                      'px-3 py-2 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400',
                      header.id === 'select' && 'w-10',
                    )}
                    style={{ width: header.getSize() }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {tableRows.map((row) => (
              <tr
                key={row.id}
                className={cn(
                  'border-b border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-900 cursor-pointer',
                  selectedFirms.has(row.original.id) &&
                    'bg-blue-50 dark:bg-blue-950/30',
                )}
                onClick={(e) =>
                  handleToggleRow(row.original.id, row.index, e.shiftKey)
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3 py-2 text-sm">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {tableRows.length === 0 && (
          <div className="flex items-center justify-center h-32 text-sm text-zinc-500">
            No firms found
          </div>
        )}
      </div>
    </div>
  );
};
